<template>
    <div v-show="can.create.schedule">
        <button-primary @click="open">
            <template slot="icon"><i class="fa fa-calendar-check-o"></i></template>
            Schreibtisch buchen
        </button-primary>

        <portal to="modal" v-if="showPopup">
            <modal @close="showPopup = false">

                <template v-slot:header>
                    Schreibtische buchen
                </template>

                <div class="mb-4">
                  <t-rich-select
                      :fetch-options="getUsers"
                      placeholder="Benutzer Suchen"
                      value-attribute="id"
                      text-attribute="full_name"
                      :minimum-input-length="1"
                      minimumInputLengthText="Bitte geben Sie ein oder mehrere Zeichen ein."
                      v-model="user"
                      searchBoxPlaceholder="Suchen..."
                      noResultsText="Keine Treffer"
                      searchingText="Suche..."
                  >
                  </t-rich-select>
                </div>

                <div class="mb-4">
                    <select-menu class="col-start-8 col-span-4" v-model="timeSelectionMode"
                                 :values="['free', 'slot', 'multi-day']" :text="['Frei', 'Slot', 'mehrere Tage']">
                        Zeitmodus
                    </select-menu>

                    <date-time-range-select v-model="range"
                                            :time-selection-mode="timeSelectionMode" :hasHalfSteps="false"
                    ></date-time-range-select>
                    <div v-show="differenceInDays > 1" class="text-gray-700 text-sm mt-2 ">Gewählte Zeitspanne:
                        {{ differenceInDays }} Tage
                    </div>
                </div>

                <div v-show="page === 'overview'">

                    <select-menu class="mb-4" v-show="floors.length > 1" v-model.number="floor"
                                 :values="floors.map(f => f.id)" :text="floors.map(f => f.name)">
                        Etage
                    </select-menu>

                    <navigation-floor :items="filteredDesks"
                                      :unavailable="filteredUnavailable"
                                      v-model="selectedIds"
                                      @selected="selectDesk"></navigation-floor>

                    <loading v-show="loading"></loading>

                    <div v-show="!loading">
                        <tailwind-table :header="['Schreibtisch', 'Typ', 'Auswählen']"
                                        v-show="filteredDesks.length > 0">
                            <tr v-for="(sug, i) in filteredDesks"
                                :class="{'bg-gray-50': i%2}"
                                @click="toggle(sug.id)">
                                <td class="px-6 py-3"><i class="fa fa-desktop"/> {{ sug.name }}</td>
                                <td class="px-6 py-3">
                                    {{ sug.owner === null ? '' : 'Mitarbeiter Schreibtisch' }}
                                </td>
                                <td class="px-2 py-3 text-right">
                                    <i class="fa fa-fw"
                                       :class="{'fa-check-square-o': isSelected(sug.id), 'fa-square-o': !isSelected(sug.id)}"></i>
                                </td>

                            </tr>
                        </tailwind-table>

                        <div v-show="filteredDesks.length === 0">Keine Schreibtische gefunden.</div>
                    </div>

                </div>

                <div v-if="selectedDesks.length > 0"
                     class="mb-4 col-span-12 sm:col-span-6">
                    <div class="text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2">Schreibtische</div>
                    <div>
                        <a v-for="desk in selectedDesks"
                           @click="toggle(desk.id)"
                           class="cursor-pointer inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-primary text-white"
                        >
                            {{ desk.name }}
                            <button type="button"
                                    class="flex-shrink-0 ml-1.5 inline-flex text-white focus:outline-none focus:text-indigo-700"
                                    aria-label="">
                                <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                    <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                </svg>
                            </button>
                        </a>

                    </div>
                    <div>
                        <loading v-show="loading"></loading>
                        <p v-show="!loading && unavailableButSelectedDesks.length > 0" class="text-red-500">
                            Einige Schreibtische sind in diesem Zeitraum nicht verfügbar:
                        </p>
                        <a v-for="desk in unavailableButSelectedDesks"
                           @click="toggle(desk.id)"
                           class="cursor-pointer inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-red-100 text-red-800"
                        >
                            {{ desk.name }}
                            <button type="button"
                                    class="flex-shrink-0 ml-1.5 inline-flex text-indigo-500 focus:outline-none focus:text-indigo-700"
                                    aria-label="">
                                <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                    <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                </svg>
                            </button>
                        </a>

                    </div>
                </div>

                <div v-show="selectedDesks.length > 0">
                    <input-text class="mb-8" v-model="comment">
                        Kommentar
                    </input-text>

                    <div class="flex justify-end">
                        <button-primary v-show="!saving" @click="create" style="background-color: rgb(248, 178, 65)!important">
                            <template v-slot:icon>
                                <i class="fa fa-calendar-check-o"></i>
                            </template>
                            Schreibtisch buchen
                        </button-primary>
                    </div>
                </div>

                <loading v-show="saving"></loading>

            </modal>
        </portal>

    </div>
</template>

<script>
import Modal from "./Modal";
import ButtonPrimary from "./UI/ButtonPrimary";
import ButtonSecondary from "./UI/ButtonSecondary";
import InputText from "./UI/InputText";
import Loading from "./UI/Loading";
import ButtonLink from "./UI/ButtonLink";
import TailwindTable from "./UI/TailwindTable";
import InputField from "./UI/InputField";
import SelectMenu from "./UI/SelectMenu";
import Navigation from "./Navigation.vue";
import {differenceInDays} from "date-fns";
import DateTimeRangeSelect from "./DateTimeRangeSelect.vue";
import TRichSelect from "vue-tailwind/dist/t-rich-select";
import NavigationFloor from "./NavigationFloor.vue";

export default {
    name: "ScheduleCreate",
    components: {
        NavigationFloor,
        Navigation,
        SelectMenu,
        InputField,
        TailwindTable,
        ButtonLink,
        Loading,
        InputText,
        ButtonSecondary,
        ButtonPrimary,
        Modal,
        DateTimeRangeSelect,
        TRichSelect
    },
    props: {
        type: {
            type: String,
            default: 'default',
        },
    },

    data: () => ({
        showPopup: false,
        loading: false,
        saving: false,
        comment: '',
        suggestions: [],

        selectedIds: [],
        unavailable: [],

        floor: null,
        floors: [],

        user: null,
        range: {
            start: (new Date()).toISOString().substring(0, 10) + ' 06:00',
            end: (new Date()).toISOString().substring(0, 10) + ' 07:00',
        },
        timeSelectionMode: "free",
    }),

    inject: ['can'],

    watch: {
        range: {
            deep: true,
            handler() {
                this.ask();
            },
        }
    },

    computed: {
        from() {
            return this.range.start.slice(0, 10);
        },
        to() {
            if (this.timeSelectionMode === "slot") {
                return this.range.start.slice(0, 10);
            }
            return this.range.end.slice(0, 10);
        },
        begin() {
            return parseInt(this.range.start.substring(11, 13));
        },
        end() {
            if (this.timeSelectionMode === "multi-day" || (this.timeSelectionMode === "slot" && (
                this.range.start.substring(11, 13) === '00'
                || this.range.start.substring(11, 13) === '12'))
            ) {
                return 24;
            }
            return parseInt(this.range.end.substring(11, 13));
        },
        selectedDesks() {
            return this.suggestions.filter(desk => this.selectedIds.includes(desk.id));
        },
        unavailableButSelectedDesks() {
            return this.unavailable.filter(desk => this.selectedIds.includes(desk.id));
        },
        filteredDesks() {
            let items = this.suggestions;
            if (this.floor) {
                items = items.filter(i => i.floor_id === this.floor);
            }

            return items;
        },
        filteredUnavailable() {
            let items = this.unavailable;
            if (this.floor) {
                items = items.filter(i => i.floor_id === this.floor);
            }

            return items;
        },
        page() {
            if (this.desk) {
                return 'confirm';
            }

            return 'overview';
        },
        differenceInDays() {
            const a = new Date(this.from);
            const b = new Date(this.to);

            return differenceInDays(b, a) + 1;
        }
    },

    methods: {
        time(val) {
            return val.substring(11, 16);
        },
        date(val) {
            let [y, m, d] = val.substring(0, 10).split('-');
            return `${d}.${m}.${y}`;
        },
        isSelected(id) {
            return this.selectedIds.indexOf(id) !== -1;
        },
        toggle(id) {
            const index = this.selectedIds.indexOf(id);

            if (index === -1) {
                this.selectedIds.push(id);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        selectDesk(item) {
            const index = this.selectedIds.indexOf(item.id);

            if (index === -1) {
                this.selectedIds.push(item.id);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        bookDesk(desk) {
            this.user = null;
            this.showPopup = true;

            if(desk){
                this.selectedIds = [desk.id];
                this.floor = desk.floor_id;
            }

            this.doAskRequest();
        },
        open() {
            this.suggestions = [];
            this.showPopup = true;
            this.ask();
        },
        getUsers(search) {
            return this.$http
                .get('/admin/users',{params: {search}})
                .then((data) => ({results: [{id:null, full_name:"Gästebuchung"},...data.data.data] }))
                .catch(error => {
                    this.$root.$emit('error', error);
                });
        },
        create() {
            this.saving = true;
            this.$http.post('/admin/reservations', {
                type: this.type,
                from: this.from,
                to: this.to,
                desk: this.selectedIds,
                comment: this.comment,
                begin: this.begin,
                end: this.end,
                user: this.user,
            })
                .then(() => {
                    this.saving = false;
                    this.showPopup = false;
                    this.$emit('created');
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                });
        },
        ask() {
            this.suggestions = [];
            this.doAskRequest();
        },
        doAskRequest() {
            this.loading = true;
            this.$http.get('/admin/reservations', {
                params: {
                    from: this.from,
                    to: this.to,
                    type: null,
                    begin: this.begin,
                    end: this.end,
                    user: this.user,
                },
            }).then(response => {
                // ignore wrong request
                if (response.data.from !== this.from && response.data.to !== this.to) {
                    return;
                }
                this.suggestions = response.data.data.filter(r => r.type === this.type);
                this.unavailable = response.data.unavailable;

                this.floors = response.data.floors;

                if (!this.floor && this.suggestions.length > 0) {
                    this.floor = this.suggestions[0].floor_id;
                }

                this.loading = false;
            }).catch(error => {
                this.$root.$emit('error', error);
                this.loading = false;
            });
        },
    }
}
</script>

<style scoped>

</style>
